import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { io } from 'socket.io-client';
import { notificationService } from '../services/api';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const toast = useToast();
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);

  const notify = (titleOrOptions, description, status = 'info') => {
    if (typeof titleOrOptions === 'object' && titleOrOptions !== null) {
      toast({
        title: titleOrOptions.title || '',
        description: titleOrOptions.description || '',
        status: titleOrOptions.status || 'info',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    toast({
      title: typeof titleOrOptions === 'string' ? titleOrOptions : String(titleOrOptions || ''),
      description: typeof description === 'string' ? description : description ? String(description) : undefined,
      status,
      duration: 4000,
      isClosable: true,
      position: 'top-right',
    });
  };

  // Fetch initial notifications from API
  useEffect(() => {
    notificationService.getNotifications()
      .then((res) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (list.length > 0) {
          setNotifications(list.map(n => ({
            id: n.id || n._id,
            title: n.title,
            message: n.message || n.body,
            time: n.time || 'Just now',
            read: n.read || false,
            type: n.type || 'info',
          })));
        }
      })
      .catch((err) => {
        console.warn('Initial notifications fetch error:', err.message);
      });
  }, []);

  // Socket.IO realtime connection
  useEffect(() => {
    const socketUrl = window.location.hostname === 'localhost' ? 'https://clikedapi.gtechclick.cloud' : window.location.origin;
    let socket;

    try {
      socket = io(socketUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnectionAttempts: 5,
      });

      socket.on('system:telemetry', (data) => {
        console.log('Realtime Telemetry:', data);
      });

      socket.on('notification:new', (n) => {
        setNotifications(prev => [n, ...(Array.isArray(prev) ? prev : [])]);
        notify(n.title, n.message || n.body, n.type || 'info');
      });
    } catch (err) {
      console.warn('Socket connection error:', err.message);
    }

    return () => {
      if (socket) socket.disconnect();
    };
  }, []);

  const markAllAsRead = () => {
    setNotifications(prev => (Array.isArray(prev) ? prev : []).map(n => ({ ...n, read: true })));
  };

  const removeNotification = (id) => {
    setNotifications(prev => (Array.isArray(prev) ? prev : []).filter(n => n.id !== id));
    notificationService.deleteNotification(id).catch(err => console.warn(err.message));
  };

  return (
    <NotificationContext.Provider value={{ notifications, messages, notify, markAllAsRead, removeNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);
